<template>
  <label v-bind="$attrs" class="checkbox-container">
    <slot name="label"
      >{{ schema.label ? schema.label : "&nbsp;" }}
      <span class="required" v-if="schema.label && schema.required"
        >*</span
      ></slot
    >
    <div v-if="hasHelpSlot" class="help">
      <font-awesome-icon icon="fas fa-circle-info" />
      <div class="tooltip" :class="schema.tooltipLocation">
        <slot name="help">Shalom from the tooltip!</slot>
      </div>
    </div>
    <input
      v-bind="$attrs"
      class="form-check-input"
      type="checkbox"
      :class="{
        'is-invalid': schema.touched && !schema.valid,
      }"
      :checked="modelValue"
      @change="
        $emit('update:modelValue', ($event.target as HTMLInputElement).checked)
      "
    />
    <span class="checkmark"></span>
  </label>
  <div name="errors" class="invalid-feedback">
    <ul>
      <li v-for="error in errors" :key="error">{{ error }}</li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { useField } from "vee-validate";
import type { FieldSchema } from "@/models/form";
import { ref, toRefs, onMounted, useSlots, computed, watch } from "vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

import * as yup from "yup";
const inputRef = ref<HTMLInputElement | null>(null);

const props = withDefaults(
  defineProps<{
    name: string;
    schema: FieldSchema;
    rules?: yup.Schema;
    modelValue: boolean | undefined;
  }>(),
  {
    name: "checkbox",
  }
);

const { name, schema } = toRefs(props);

const validationSchema = computed(() => {
  if (props.schema.required) {
    return yup
      .boolean()
      .oneOf([true], schema.value.errorMessage)
      .required(schema.value.errorMessage);
  }

  return yup.boolean().notRequired();
});

const { value, errors, setValue } = useField(name, validationSchema);

const slots = useSlots();

const hasHelpSlot = computed<boolean>(() => {
  return !!slots["help"];
});

onMounted(() => {
  if (props.modelValue) {
    setValue(props.modelValue);
  }
});

defineExpose({
  errors,
  setValue,
  value,
  inputRef,
});

watch(
  () => props.modelValue,
  () => {
    setValue(props.modelValue);
  }
);
</script>

<style scoped lang="scss">
.checkbox-container {
  font-family: var(--body-font);
  font-weight: 400;
  display: block;
  position: relative;
  padding-left: 24px;
  margin-bottom: 12px;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  &[disabled="true"] {
    cursor: default;
    input {
      pointer-events: none;
    }

    .checkmark {
      opacity: 0.5;
      cursor: default;
    }
  }

  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;

    &:checked ~ .checkmark {
      background-color: var(--theme-color);

      &:after {
        display: block;
      }
    }
  }

  .checkmark {
    position: absolute;
    top: 2px;
    left: 0;
    height: 21px;
    width: 21px;
    background-color: #fff;
    border: 1px solid #ced4da;
    border-radius: 4px;
    cursor: pointer;

    &:after {
      content: "";
      position: absolute;
      display: none;
      left: 6px;
      top: 0px;
      width: 8px;
      height: 14px;
      border: solid white;
      border-width: 0 3px 3px 0;
      transform: rotate(45deg);
    }
  }

  .checkmark:after {
    left: 6px;
    top: 0px;
    width: 8px;
    height: 14px;
    border: solid white;
    border-width: 0 3px 3px 0;
    transform: rotate(45deg);
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
  }
}

.checkbox-container:is(.hasHelpSlot) {
  display: flex;
  flex-direction: row;
}

.invalid-feedback ul {
  list-style: none;
  color: #8b0e04;
  padding-left: 0;
  margin: 0 0 0.5em;
}

.help {
  display: flex;
}

.help .tooltip {
  display: block;
  opacity: 0;
  background-color: white;
}

.help:hover .tooltip,
.help:focus .tooltip {
  opacity: 1;
}

.checkbox-wrapper input {
  height: 21px !important;
}

.checkmark[checked="true"] {
  background-color: black;
  color: white;
}
</style>
